import React, {
  createContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  CAMERA_MODE,
  RECORDING_MODE,
  RECORDING_STATUS,
} from "../../utils/constants";

export const MediaCaptureContext = createContext({
  videoRef: null,
  recordingStatus: RECORDING_STATUS.idle,
  mediaUrl: "",
  mediaBlob: null,
  initializeStream: () => {},
  startRecording: () => {},
  stopRecording: () => {},
  discardRecording: () => {},
  switchCamera: () => {},
  takePhoto: () => {},
  discardPhoto: () => {},
});

export const MediaCaptureProvider = ({
  mode = RECORDING_MODE.video,
  videoResolution = { width: 320, height: 180, frameRate: 24 },
  defaultMediaUrl = "",
  children,
}) => {
  const videoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [mediaUrl, setMediaUrl] = useState(defaultMediaUrl);
  const [mediaBlob, setMediaBlob] = useState(null);
  const [recordingStatus, setRecordingStatus] = useState(RECORDING_STATUS.idle);
  const [facingMode, setFacingMode] = useState(CAMERA_MODE.user); // 'user' for front, 'environment' for back

  // Initialize media stream
  const initializeStream = useCallback(async () => {
    try {

      // set video stream null by default unless we use de `video` mode
      let stream = null;
      if([RECORDING_MODE.video, RECORDING_MODE.photo].includes(mode)) {
        let additionalStreamProps = {};
        if(mode === RECORDING_MODE.video) {
          additionalStreamProps = videoResolution;
        }
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode, ...additionalStreamProps },
        });
      }

      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      let streams = [];
      if ([RECORDING_MODE.video, RECORDING_MODE.photo].includes(mode)) {
        streams = [...stream.getVideoTracks(), ...audioStream.getAudioTracks()];
      } else {
        streams = [...audioStream.getAudioTracks()];
      }

      const combinedStream = new MediaStream(streams);
      mediaStreamRef.current = combinedStream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing media devices:", err);
    }
  }, [facingMode, mode]);

  // Cleanup media stream on unmount
  const cleanupStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  useEffect(() => {
    initializeStream();

    return () => {
      cleanupStream();
    };
  }, [initializeStream, cleanupStream]);

  // Start recording video
  const startRecording = () => {
    if (mediaStreamRef.current) {
      let chunks = [];
      let mimeType = [RECORDING_MODE.video, RECORDING_MODE.photo].includes(mode) ? "video/mp4" : "audio/mp4";
      // if is using a Firefox browser
      if(typeof InstallTrigger !== 'undefined') {
        mimeType = [RECORDING_MODE.video, RECORDING_MODE.photo].includes(mode) ? "video/webm" : "audio/webm";
      }

      const recorder = new MediaRecorder(mediaStreamRef.current, {
        mimeType
      });
      mediaRecorderRef.current = recorder;
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      mediaRecorderRef.current.onstop = () => {
        const mediaBlob = new Blob(chunks, { type: mimeType });        
        const _mediaUrl = URL.createObjectURL(mediaBlob);
        setMediaUrl(_mediaUrl);
        setMediaBlob(mediaBlob); // <- Esto es lo nuevo
        setRecordingStatus(RECORDING_STATUS.stopped);
      };
      mediaRecorderRef.current.start();
      setRecordingStatus(RECORDING_STATUS.recording);
    }
  };

  // Stop recording video
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
  };

  // Discard recorded video
  const discardRecording = () => {
    setRecordingStatus(RECORDING_STATUS.idle);
    setMediaUrl("");
    if(videoRef.current && [RECORDING_MODE.video, RECORDING_MODE.photo].includes(mode)) {
      videoRef.current.load();
    }
  };

  // Switch camera between front and back
  const switchCamera = () => {
    if(recordingStatus !== RECORDING_STATUS.idle) return;
    setFacingMode((prev) =>
      prev === CAMERA_MODE.user ? CAMERA_MODE.environment : CAMERA_MODE.user
    );
  };

  // Take photo from video
  const takePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        setMediaUrl(URL.createObjectURL(blob));
        setMediaBlob(blob);
      }, "image/png");
    }
  };

  const discardPhoto = () => {
    setMediaUrl("");
    setMediaBlob(null);
  };

  return (
    <MediaCaptureContext.Provider
      value={{
        videoRef,
        recordingStatus,
        mediaUrl,
        mediaBlob,
        initializeStream,
        startRecording,
        stopRecording,
        discardRecording,
        switchCamera,
        takePhoto,
        discardPhoto,
      }}
    >
      {children}
    </MediaCaptureContext.Provider>
  );
};
